/**
 * Public About page. Linked from the footer; reachable without auth on
 * asktogether.co.uk (the public visitor host). Tells anyone who lands on
 * a join URL who's behind the service and what the tool does.
 *
 * Substantive privacy/data wording lives in PrivacyPage and CookiesPage.
 * Keep this page about the product, not the data minutiae.
 */
export default function AboutPage() {
  return (
    <main className="prose">
      <p style={{ marginBottom: "0.5rem" }}>
        <a href="/join">← Back</a>
      </p>
      <h1>About AskTogether</h1>

      <h2>What it is</h2>
      <p>
        AskTogether is a live Q&amp;A tool for events, meetings, and
        classroom-style sessions. The organiser opens a session, attendees
        join with a six-character code (or a scanned QR), and questions are
        surfaced, deduplicated, and moderated in real time. Attendees can
        also add their name to an existing question — &ldquo;I want to know
        this too&rdquo; — instead of asking the same thing again.
      </p>

      <h2>What it's for</h2>
      <p>
        Most Q&amp;A is bottlenecked by the loudest voice in the room. The
        goal here is to make it easier for everyone to contribute,
        anonymously if they want to, and to give the moderator a clear
        view of what the room actually wants to ask. Similar questions are
        clustered automatically so the moderator can take them as a group
        rather than working through twelve variations of the same thing.
      </p>

      <h2>How it works</h2>
      <ul>
        <li>
          <strong>Join</strong> — type the join code (or scan the QR), pick
          a display name, or join anonymously. No account needed.
        </li>
        <li>
          <strong>Ask</strong> — as you type, the system surfaces existing
          questions that look similar. You can join an existing one rather
          than submit a duplicate.
        </li>
        <li>
          <strong>Moderate</strong> — the organiser (and any moderators
          they've added) sees a queue, with similar items grouped, and can
          approve, reject, or mark questions as answered.
        </li>
        <li>
          <strong>See the board</strong> — approved questions appear on
          everyone's screen, sorted by signal (voices, upvotes), updating
          live.
        </li>
      </ul>

      <h2>What runs the matching</h2>
      <p>
        Questions are embedded with an open-source model
        (<code>nomic-embed-text</code>) running on infrastructure we
        control. Embeddings are stored in a vector database keyed by
        session, so questions from one session never influence matching in
        another. The matching threshold is per-session — the organiser can
        loosen or tighten it from the session setup tab.
      </p>

      <h2>Who runs it</h2>
      <p>
        AskTogether is operated by <strong>Time Back Solutions Ltd</strong>,
        a company registered in England and Wales. AskTogether™ is a trade
        mark of Time Back Solutions Ltd.
      </p>
      <p>
        For privacy, data, or accessibility queries see the{" "}
        <a href="/privacy">privacy notice</a> and the{" "}
        <a href="/cookies">cookies page</a>.
      </p>
    </main>
  );
}
